import { useRef } from 'react';
import { Formik, Field, FormikHelpers } from 'formik';
import { DateTime } from 'luxon';
import i18n from 'i18next';

import {
  Modal,
  Button,
  Select,
  Tooltip,
  DatePickerField,
} from 'web-components';

import { Flex, Text } from 'web-styled';
import { useEventListener } from 'web-hooks';

import * as S from './styled';
import { OpenedTooltip } from 'web-types';

// Types
interface Option {
  value: string;
  label: string;
}

interface WorkoutEventFormValues {
  client?: string;
  workout?: string;
  originalId?: string;
  start: DateTime;
  end: DateTime;
}

interface Props {
  isOpen: boolean;
  clients: Option[];
  workouts: Option[];
  initialValues: WorkoutEventFormValues;
  openedTooltips: OpenedTooltip[];
  onSubmit: (
    data: WorkoutEventFormValues,
    actions: FormikHelpers<WorkoutEventFormValues>
  ) => void | Promise<void>;
  onCancelTooltip: () => void;
  onLogActivity: (startDate: DateTime) => void;
  onRequestClose: () => void;
  onUserSelected: (userId: number) => void;
  onRequestCreateWorkout: (values: WorkoutEventFormValues) => void;
}

// Constants
const selectProps = {
  styles: {
    container: (provided: any) => ({
      ...provided,
      width: '100%',
    }),
    menu: (provided: any) => ({
      ...provided,
      marginTop: '1px',
    }),
  },
  theme: (theme: any) => ({
    ...theme,
    borderRadius: 3,
  }),
};

// Component
const NewWorkoutEventModal = ({
  isOpen,
  clients,
  workouts,
  initialValues,
  openedTooltips,
  onSubmit,
  onLogActivity,
  onRequestClose,
  onUserSelected,
  onCancelTooltip,
  onRequestCreateWorkout,
}: Props) => {
  const target = useRef<HTMLDivElement>(null);

  useEventListener(
    'click',
    () => {
      onCancelTooltip();
    },
    {
      target: target.current,
      disabled: !isOpen,
    }
  );

  const handleClientChange = (
    option: Option | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (option) {
      setFieldValue('client', option.value);
      setFieldValue('workout', null);
      onUserSelected(Number(option.value));
    }
  };

  const handleWorkoutChange = (
    option: Option | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (option) {
      setFieldValue('workout', option.value);
    }
  };

  const handleStartDateChange = (
    date: Date,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const start = DateTime.fromJSDate(date);
    setFieldValue('start', start);
    setFieldValue('end', start.plus({ hours: 1 }));
  };

  const handleEndDateChange = (
    date: Date,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue('end', DateTime.fromJSDate(date));
  };

  const isScheduleTooltipOpen = openedTooltips.some(
    (el) => el.key === 'schedule_workout_form'
  );

  return (
    <Modal
      title={
        initialValues.originalId ? 'Edit' : i18n.t('schedule.new.workout')
      }
      isOpen={isOpen}
      contentStyle={{ width: 400 }}
      onRequestClose={onRequestClose}
    >
      <Formik<WorkoutEventFormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {(formikProps) => {
          const selectedClient = clients.find(
            (option) => option.value === String(formikProps.values.client)
          );

          const selectedWorkout = workouts.find(
            (option) => option.value === formikProps.values.workout
          );

          const isFormValid =
            formikProps.values.start &&
            formikProps.values.end &&
            formikProps.values.workout &&
            formikProps.values.client;

          return (
            <Flex
              as="form"
              ref={target}
              column
              width="100%"
              onSubmit={formikProps.handleSubmit}
            >
              {!initialValues.originalId && (
                <Flex width="100%" justify="flex-end">
                  <Button
                    appearance="link"
                    onClick={() => onLogActivity(formikProps.values.start)}
                  >
                    Log Activity instead
                  </Button>
                </Flex>
              )}

              <Tooltip
                body={i18n.t('tooltip.calendar.schedule.modal')}
                isOpen={isScheduleTooltipOpen}
              >
                <Flex width="100%" column>
                  <S.Label htmlFor="client-select">
                    {i18n.t('client')}
                  </S.Label>
                  <Select
                    {...selectProps}
                    id="client-select"
                    value={selectedClient || null}
                    onChange={(option: Option | null) =>
                      handleClientChange(option, formikProps.setFieldValue)
                    }
                    onBlur={formikProps.handleBlur}
                    name="client"
                    options={clients}
                  />
                </Flex>
              </Tooltip>

              <Flex width="100%" column>
                <S.Label>Time</S.Label>
                <Flex spacing={10}>
                  <Field
                    name="start"
                    dateFormat="MM/dd/yyyy   hh:mm a"
                    showTimeSelect
                    minDate={new Date()}
                    onChange={(date: Date) =>
                      handleStartDateChange(date, formikProps.setFieldValue)
                    }
                    component={DatePickerField}
                  />
                  <Field
                    name="end"
                    selected={new Date(formikProps.values.start.toISO())}
                    dateFormat="MM/dd/yyyy   hh:mm a"
                    showTimeSelect
                    minDate={new Date(formikProps.values.start.toISO())}
                    onChange={(date: Date) =>
                      handleEndDateChange(date, formikProps.setFieldValue)
                    }
                    component={DatePickerField}
                  />
                </Flex>
              </Flex>

              <Flex width="100%" column>
                <S.Label htmlFor="workout-select">
                  {i18n.t('Workout')}
                </S.Label>
                <Select
                  {...selectProps}
                  id="workout-select"
                  value={selectedWorkout || null}
                  onBlur={formikProps.handleBlur}
                  onChange={(option: Option | null) =>
                    handleWorkoutChange(option, formikProps.setFieldValue)
                  }
                  name="workout"
                  options={workouts}
                />
              </Flex>

              <Flex
                width="100%"
                align="center"
                justify="space-between"
                margin="15px 0 0 0"
              >
                <Text color="#9ea0a1">Or you can</Text>
                <Button
                  ghost
                  appearance="primary"
                  onClick={() => onRequestCreateWorkout(formikProps.values)}
                  isDisabled={formikProps.isSubmitting}
                >
                  {i18n.t('create.new.workout')}
                </Button>
              </Flex>

              <S.Footer>
                <hr />
                <Flex width="100%" justify="flex-end" spacing={20}>
                  <Button ghost appearance="danger" onClick={onRequestClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    appearance="primary"
                    isLoading={formikProps.isSubmitting}
                    isDisabled={formikProps.isSubmitting || !isFormValid}
                  >
                    Schedule
                  </Button>
                </Flex>
              </S.Footer>
            </Flex>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default NewWorkoutEventModal;
